import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Users, Network, FileText, GraduationCap, Plus, Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import OrgTree from "./hr/OrgTree";
import { PositionDialog, DocumentDialog, CourseDialog, EnrollmentDialog } from "./hr/HRDialogs";
import { farmDateIso } from "@/lib/farmTime";

export default function HRPage() {
    const [summary, setSummary] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [positions, setPositions] = useState([]);
    const [docs, setDocs] = useState([]);
    const [courses, setCourses] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [dlg, setDlg] = useState(null);

    const load = useCallback(async () => {
        try {
            const [s, e, p, d, c, en] = await Promise.all([
                api.get("/hr/summary").then(r => r.data),
                api.get("/employees").then(r => r.data).catch(() => []),
                api.get("/org-positions").then(r => r.data),
                api.get("/employee-documents").then(r => r.data),
                api.get("/training-courses").then(r => r.data),
                api.get("/training-enrollments").then(r => r.data),
            ]);
            setSummary(s);
            setEmployees(e || []);
            setPositions(p || []);
            setDocs(d || []);
            setCourses(c || []);
            setEnrollments(en || []);
        } catch (err) { toast.error("Failed to load HR data"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (path, body) => {
        try {
            if (body.id) { const { id, ...rest } = body; await api.put(`/${path}/${id}`, rest); }
            else await api.post(`/${path}`, body);
            toast.success("Saved");
            setDlg(null);
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    };

    const del = async (path, id, label) => {
        if (!window.confirm(`Delete ${label}?`)) return;
        try { await api.delete(`/${path}/${id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error("Delete failed"); }
    };

    const employeeMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
    const positionsByParent = useMemo(() => {
        const g = {};
        positions.forEach(p => {
            const key = p.parent_id || "__root__";
            g[key] = g[key] || [];
            g[key].push(p);
        });
        return g;
    }, [positions]);

    if (!summary) return <div className="p-6 text-muted-foreground">{i18n.t("batch1.33ce417454bf")}</div>;
    const k = summary.kpis;

    return (
        <div className="space-y-6" data-testid="hr-page">
            <div>
                <div className="tiny-label flex items-center gap-2"><Users className="w-4 h-4" />{i18n.t("batch1.9b864cbc52c7")}</div>
                <h1 className="text-3xl font-bold mt-1">{i18n.t("batch1.7d521bd3eaf5")}</h1>
                <p className="text-sm text-muted-foreground">{i18n.t("batch1.e8c2bdd8592c")}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
                <Kpi label={i18n.t("batch1.8c5b6be28fbd")} value={k.employees} testid="hr-kpi-employees" />
                <Kpi label={i18n.t("batch1.a733b809d2f1")} value={k.active_employees} testid="hr-kpi-active" />
                <Kpi label={i18n.t("batch1.cd4683fadf70")} value={k.positions} testid="hr-kpi-positions" />
                <Kpi label={i18n.t("batch1.687c82861c95")} value={k.documents} testid="hr-kpi-documents" />
                <Kpi label={i18n.t("batch1.a16536d78dc3")} value={k.courses} testid="hr-kpi-courses" />
                <Kpi
                    label={i18n.t("batch1.86566cf7ad54")}
                    value={k.enrollments}
                    sub={`${k.completed} done · ${k.expiring_soon || 0} expiring · ${k.expired || 0} lapsed`}
                    testid="hr-kpi-enrollments"
                    tone={k.expired ? "red" : "slate"}
                />
            </div>

            <Tabs defaultValue="orgchart">
                <TabsList data-testid="hr-tabs">
                    <TabsTrigger value="orgchart" data-testid="hr-tab-orgchart"><Network className="w-4 h-4 mr-1" />{i18n.t("batch1.9890885ac526")}</TabsTrigger>
                    <TabsTrigger value="documents" data-testid="hr-tab-documents"><FileText className="w-4 h-4 mr-1" />{i18n.t("batch1.687c82861c95")}</TabsTrigger>
                    <TabsTrigger value="training" data-testid="hr-tab-training"><GraduationCap className="w-4 h-4 mr-1" />{i18n.t("batch1.b6fe7f5e7917")}</TabsTrigger>
                </TabsList>

                <TabsContent value="orgchart" className="space-y-3">
                    <div className="flex justify-end">
                        <Button onClick={() => setDlg({ kind: "position", data: {} })} data-testid="new-position-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("batch1.a8a08b39323e")}
                        </Button>
                    </div>
                    <Card className="p-4">
                        {positions.length === 0 && (
                            <div className="text-sm text-muted-foreground py-4">
                                {i18n.t("batch1.87ab2daac1b7")}
                            </div>
                        )}
                        <OrgTree
                            groups={positionsByParent}
                            employees={employeeMap}
                            onEdit={(p) => setDlg({ kind: "position", data: p })}
                            onDelete={(p) => del("org-positions", p.id, p.title)}
                        />
                    </Card>
                </TabsContent>

                <TabsContent value="documents" className="space-y-3">
                    <div className="flex justify-end">
                        <Button onClick={() => setDlg({ kind: "document", data: {} })} data-testid="new-document-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("batch1.be059c99af08")}
                        </Button>
                    </div>
                    <Card className="overflow-hidden">
                        <DocsTable docs={docs} employeeMap={employeeMap} onEdit={(d) => setDlg({ kind: "document", data: d })} onDelete={(d) => del("employee-documents", d.id, d.doc_type)} />
                    </Card>
                </TabsContent>

                <TabsContent value="training" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <CoursesCard
                            courses={courses}
                            onNew={() => setDlg({ kind: "course", data: {} })}
                            onEdit={(c) => setDlg({ kind: "course", data: c })}
                            onDelete={(c) => del("training-courses", c.id, c.name)}
                        />
                        <EnrollmentsCard
                            enrollments={enrollments}
                            employeeMap={employeeMap}
                            courses={courses}
                            onNew={() => setDlg({ kind: "enrollment", data: {} })}
                            onEdit={(en) => setDlg({ kind: "enrollment", data: en })}
                            onDelete={(en) => del("training-enrollments", en.id, "enrollment")}
                        />
                    </div>
                </TabsContent>
            </Tabs>

            {dlg?.kind === "position" && (
                <PositionDialog initial={dlg.data} positions={positions} employees={employees}
                    onSave={(b) => save("org-positions", b)} onClose={() => setDlg(null)} />
            )}
            {dlg?.kind === "document" && (
                <DocumentDialog initial={dlg.data} employees={employees}
                    onSave={(b) => save("employee-documents", b)} onClose={() => setDlg(null)} />
            )}
            {dlg?.kind === "course" && (
                <CourseDialog initial={dlg.data}
                    onSave={(b) => save("training-courses", b)} onClose={() => setDlg(null)} />
            )}
            {dlg?.kind === "enrollment" && (
                <EnrollmentDialog initial={dlg.data} employees={employees} courses={courses}
                    onSave={(b) => save("training-enrollments", b)} onClose={() => setDlg(null)} />
            )}
        </div>
    );
}

function Kpi({ label, value, sub, testid, tone = "slate" }) {
    const cls = tone === "red"
        ? "bg-destructive/10 border-destructive/40 text-destructive"
        : "bg-secondary border-border";
    return (
        <Card className={`p-3 border ${cls}`} data-testid={testid}>
            <div className="tiny-label">{label}</div>
            <div className="text-2xl font-bold mt-1">{formatNumber(value)}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </Card>
    );
}

function DocsTable({ docs, employeeMap, onEdit, onDelete }) {
    return (
        <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr className="text-left">
                <Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.6cb0c2af765e")}</Th><Th>{i18n.t("batch1.2c3cafa4db3f")}</Th><Th>{i18n.t("batch1.deb5d5e2f038")}</Th><Th>{i18n.t("batch1.a99be3da0c9d")}</Th><Th></Th>
            </tr></thead>
            <tbody>
                {docs.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted-foreground py-6">{i18n.t("batch1.b1e64785455f")}</td></tr>
                )}
                {docs.map((d) => (
                    <tr key={d.id} className="border-t" data-testid={`doc-row-${d.id}`}>
                        <td className="p-2 font-medium">{employeeMap[d.employee_id]?.name || d.employee_name || "—"}</td>
                        <td className="p-2 capitalize">{d.doc_type}</td>
                        <td className="p-2">
                            {d.file_url
                                ? <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{i18n.t("batch1.cf9b77061f7b")}</a>
                                : d.file_name || "—"}
                        </td>
                        <td className="p-2 text-xs">{d.issued_date || "—"}</td>
                        <td className="p-2 text-xs">{d.expiry_date || "—"}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => onEdit(d)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(d)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function CoursesCard({ courses, onNew, onEdit, onDelete }) {
    return (
        <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">{i18n.t("batch1.96e7788c79bd")}{courses.length})</h3>
                <Button size="sm" onClick={onNew} data-testid="new-course-btn"><Plus className="w-4 h-4 mr-1" />{i18n.t("batch1.6403f2b7eb2a")}</Button>
            </div>
            {courses.length === 0 && <div className="text-sm text-muted-foreground">{i18n.t("batch1.d35ccc935460")}</div>}
            <div className="space-y-1.5">
                {courses.map((c) => (
                    <div key={c.id} className="flex items-center justify-between border rounded-md px-2 py-1.5" data-testid={`course-row-${c.id}`}>
                        <div className="min-w-0">
                            <div className="font-medium truncate">{c.name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                                {c.category || "general"} · {c.validity_months ? `${c.validity_months}mo` : "no expiry"}
                            </div>
                        </div>
                        <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => onEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(c)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}

function EnrollmentsCard({ enrollments, employeeMap, courses, onNew, onEdit, onDelete }) {
    const today = farmDateIso();
    return (
        <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">{i18n.t("batch1.9bb44178089c")}{enrollments.length})</h3>
                <Button size="sm" onClick={onNew} data-testid="new-enrollment-btn"><Plus className="w-4 h-4 mr-1" />{i18n.t("batch1.8bd5eb3853f4")}</Button>
            </div>
            {enrollments.length === 0 && <div className="text-sm text-muted-foreground">{i18n.t("batch1.54f25815e591")}</div>}
            <div className="space-y-1.5">
                {enrollments.map((en) => {
                    const emp = employeeMap[en.employee_id];
                    const course = courses.find(c => c.id === en.course_id);
                    const expired = en.expiry_date && en.expiry_date < today;
                    return (
                        <div key={en.id}
                            className={`flex items-center justify-between border rounded-md px-2 py-1.5 ${expired ? "border-destructive/40 bg-destructive/5" : ""}`}
                            data-testid={`enrollment-row-${en.id}`}
                        >
                            <div className="min-w-0">
                                <div className="font-medium truncate">{emp?.name || en.employee_name || "—"}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {course?.name || en.course_name || "—"} ·{" "}
                                    <Badge variant="outline" className="capitalize text-xs">{en.status || "assigned"}</Badge>
                                    {en.expiry_date && (
                                        <span className={expired ? "text-destructive font-semibold ml-1" : "ml-1"}>
                                            {i18n.t("batch1.134fad54d6c5")} {en.expiry_date}{expired ? " (LAPSED)" : ""}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => onEdit(en)}><Pencil className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(en)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}

function Th({ children }) {
    return <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{children}</th>;
}
